import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

const getSavedEmailsStorageKey = chatId => `ladder_saved_emails_${chatId}`;

const loadSavedEmailsFromStorage = chatId => {
  if (!chatId) return new Set();
  try {
    const stored = localStorage.getItem(getSavedEmailsStorageKey(chatId));
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (error) {
    console.error('❌ Error loading saved emails from storage:', error);
  }
  return new Set();
};

const saveSavedEmailsToStorage = (chatId, emailsSet) => {
  if (!chatId) return;
  try {
    localStorage.setItem(getSavedEmailsStorageKey(chatId), JSON.stringify(Array.from(emailsSet)));
  } catch (error) {
    console.error('❌ Error saving emails to storage:', error);
  }
};

/**
 * Chat session management: listing, loading messages, CRUD for chats, and saved-email bookkeeping.
 */
export function useChatSessions({
  userId,
  savedAlumniRows,
  results,
  formatChatMessagesToResults,
  setResults,
  setCandidateSaved,
  setCandidateEmails,
  showToast,
}) {
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showChatSidebar, setShowChatSidebar] = useState(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [creatingNewChat, setCreatingNewChat] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [newChatName, setNewChatName] = useState('');
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingChatName, setEditingChatName] = useState('');
  const [currentChatSavedEmails, setCurrentChatSavedEmails] = useState(new Set());

  const persistCurrentSaved = useCallback((chatId, emailsSet) => {
    saveSavedEmailsToStorage(chatId, emailsSet);
    setCurrentChatSavedEmails(new Set(emailsSet));
  }, []);

  const loadChatMessages = useCallback(
    async chatId => {
      if (!userId || !chatId) return;
      setResults([]);
      setCandidateSaved({});
      setCandidateEmails({});
      setCurrentChatSavedEmails(new Set());
      try {
        const response = await apiFetch(`/chats/${chatId}/messages?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.messages)) {
            const formatted = formatChatMessagesToResults(data.messages);
            let lastUserQueryIndex = -1;
            formatted.forEach((result, idx) => {
              if (result.type === 'user') {
                lastUserQueryIndex = idx;
              }
            });
            const visibleResults = lastUserQueryIndex >= 0 ? formatted.slice(lastUserQueryIndex) : formatted;
            setResults(visibleResults);

            const savedAlumniByEmail = new Map();
            const chatSpecificRows = savedAlumniRows.filter(row => row.chat_id === chatId);
            chatSpecificRows.forEach(row => {
              if (row.alumni_email && row.alumni_email.trim()) {
                savedAlumniByEmail.set(row.alumni_email.trim().toLowerCase(), row);
              }
            });

            const savedEmails = new Set();
            const newCandidateSaved = {};
            const newCandidateEmails = {};

            visibleResults.forEach(result => {
              if (result.type === 'assistant' && Array.isArray(result.content)) {
                result.content.forEach(item => {
                  const email = item.jobs_email || item.email;
                  if (email && email.trim()) {
                    const emailLower = email.trim().toLowerCase();
                    const savedAlumni = savedAlumniByEmail.get(emailLower);
                    if (savedAlumni) {
                      savedEmails.add(emailLower);
                      if (item.id) {
                        newCandidateSaved[item.id] = true;
                        newCandidateEmails[item.id] = email;
                      }
                    }
                  }
                });
              }
            });

            setCandidateSaved(newCandidateSaved);
            setCandidateEmails(prev => ({ ...prev, ...newCandidateEmails }));
            persistCurrentSaved(chatId, savedEmails);
          } else {
            setResults([]);
          }
        } else {
          setResults([]);
        }
      } catch (error) {
        setResults([]);
      }
    },
    [formatChatMessagesToResults, persistCurrentSaved, savedAlumniRows, setCandidateEmails, setCandidateSaved, setResults, userId],
  );

  const loadChatHistory = useCallback(async () => {
    if (!userId) {
      setChats([]);
      setCurrentChatId(null);
      return;
    }
    setLoadingChats(true);
    try {
      const response = await apiFetch(`/chats?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.chats)) {
          setChats(data.chats);
          if (data.chats.length > 0) {
            const activeChat = data.chats.find(chat => chat.is_active) || data.chats[0];
            setCurrentChatId(activeChat.id);
            const savedFromStorage = loadSavedEmailsFromStorage(activeChat.id);
            setCurrentChatSavedEmails(savedFromStorage);
            setResults([]);
          }
        } else {
          setChats([]);
          setCurrentChatId(null);
        }
      } else {
        setChats([]);
        setCurrentChatId(null);
      }
    } catch (error) {
      setChats([]);
      setCurrentChatId(null);
    } finally {
      setLoadingChats(false);
    }
  }, [setResults, userId]);

  // Re-sync candidateSaved state when savedAlumniRows change (e.g., after refresh) using current chat view.
  useEffect(() => {
    if (!currentChatId || results.length === 0) return;

    const chatSpecificRows = savedAlumniRows.filter(row => row.chat_id === currentChatId);
    const savedAlumniByEmail = new Map();
    chatSpecificRows.forEach(row => {
      if (row.alumni_email && row.alumni_email.trim()) {
        savedAlumniByEmail.set(row.alumni_email.trim().toLowerCase(), row);
      }
    });

    const newCandidateSaved = {};
    const newCandidateEmails = {};
    const savedEmails = new Set();

    results.forEach(result => {
      if (result.type === 'assistant' && Array.isArray(result.content)) {
        result.content.forEach(item => {
          const email = item.jobs_email || item.email;
          if (email && email.trim() && item.id) {
            const emailLower = email.trim().toLowerCase();
            if (savedAlumniByEmail.has(emailLower)) {
              savedEmails.add(emailLower);
              newCandidateSaved[item.id] = true;
              newCandidateEmails[item.id] = email;
            }
          }
        });
      }
    });

    setCandidateSaved(prev => {
      const hasChanges =
        Object.keys(newCandidateSaved).some(id => newCandidateSaved[id] !== prev[id]) ||
        Object.keys(prev).some(id => !newCandidateSaved[id] && prev[id]);
      return hasChanges ? newCandidateSaved : prev;
    });

    setCandidateEmails(prev => {
      const hasChanges = Object.keys(newCandidateEmails).some(id => newCandidateEmails[id] !== prev[id]);
      return hasChanges ? { ...prev, ...newCandidateEmails } : prev;
    });

    setCurrentChatSavedEmails(prev => {
      if (prev.size !== savedEmails.size || Array.from(prev).some(email => !savedEmails.has(email))) {
        return savedEmails;
      }
      return prev;
    });
  }, [currentChatId, results, savedAlumniRows, setCandidateEmails, setCandidateSaved]);

  const createNewChat = useCallback(
    async name => {
      if (!userId || !name.trim() || isCreatingChat) return;
      setIsCreatingChat(true);
      try {
        const response = await apiFetch(`/chats?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim() }),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.chat) {
            setChats(prev => [data.chat, ...prev]);
            setCurrentChatId(data.chat.id);
            await apiFetch(`/chats/${data.chat.id}/activate?user_id=${userId}`, {
              method: 'POST',
            });
            setNewChatName('');
            setCreatingNewChat(false);
            setShowChatSidebar(false);
            setResults([]);
            setCurrentChatSavedEmails(new Set());
          }
        }
      } catch (error) {
        console.error('❌ Error creating new chat:', error);
      } finally {
        setIsCreatingChat(false);
      }
    },
    [isCreatingChat, setResults, userId],
  );

  const switchToChat = useCallback(
    async chatId => {
      if (!userId || chatId === currentChatId) return;
      try {
        const response = await apiFetch(`/chats/${chatId}/activate?user_id=${userId}`, {
          method: 'POST',
        });
        if (response.ok) {
          setCurrentChatId(chatId);
          await loadChatMessages(chatId);
        }
      } catch (error) {
        console.error('❌ Error switching to chat:', error);
      }
    },
    [currentChatId, loadChatMessages, userId],
  );

  const updateChatName = useCallback(
    async (chatId, newName) => {
      if (!userId || !newName.trim()) return;
      try {
        const response = await apiFetch(`/chats/${chatId}?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName.trim() }),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.chat) {
            setChats(prev => prev.map(chat => (chat.id === chatId ? { ...chat, name: data.chat.name } : chat)));
          }
        }
      } catch (error) {
        console.error('❌ Error updating chat name:', error);
      }
      setEditingChatId(null);
      setEditingChatName('');
    },
    [userId],
  );

  const deleteChat = useCallback(
    async chatId => {
      if (!userId) return;
      if (!window.confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
        return;
      }
      try {
        const response = await apiFetch(`/chats/${chatId}?user_id=${userId}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          setChats(prev => prev.filter(chat => chat.id !== chatId));
          if (chatId === currentChatId) {
            const remaining = chats.filter(chat => chat.id !== chatId);
            if (remaining.length > 0) {
              await switchToChat(remaining[0].id);
            } else {
              await createNewChat('New Chat');
            }
          }
        } else {
          showToast?.('Failed to delete chat. Please try again.', 'error');
        }
      } catch (error) {
        showToast?.('Error deleting chat. Please try again.', 'error');
      }
    },
    [chats, createNewChat, currentChatId, showToast, switchToChat, userId],
  );

  const stateSummary = useMemo(
    () => ({
      chats,
      currentChatId,
      showChatSidebar,
      loadingChats,
      creatingNewChat,
      isCreatingChat,
      newChatName,
      editingChatId,
      editingChatName,
      currentChatSavedEmails,
    }),
    [chats, currentChatId, showChatSidebar, loadingChats, creatingNewChat, isCreatingChat, newChatName, editingChatId, editingChatName, currentChatSavedEmails],
  );

  return {
    ...stateSummary,
    setShowChatSidebar,
    setCreatingNewChat,
    setIsCreatingChat,
    setNewChatName,
    setEditingChatId,
    setEditingChatName,
    setCurrentChatId,
    loadChatHistory,
    loadChatMessages,
    createNewChat,
    switchToChat,
    updateChatName,
    deleteChat,
    setCurrentChatSavedEmails,
    setCurrentChatId,
  };
}

export { getSavedEmailsStorageKey, loadSavedEmailsFromStorage, saveSavedEmailsToStorage };
